import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Building2,
  Clock,
  Copy,
  Download,
  ExternalLink,
  Eye,
  Link as LinkIcon,
  Loader2,
  MapPin,
  QrCode,
  ScanLine,
  Star,
} from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function PortalBusinessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get(`/portal/businesses/${id}`),
        api.get(`/portal/businesses/${id}/scans`, { params: { limit: 25 } }),
      ]);
      setBusiness(b.data);
      setScans(s.data.scans || []);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/customer/business");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} kopiert.`);
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const downloadQr = () => {
    if (!business) return;
    const url = `${API_BASE}/portal/businesses/${business.id}/qr.png?size=1024`;
    const token = localStorage.getItem("1ai_token");
    fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("HTTP " + r.status))))
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `1ai-review-${business.slug}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Download fehlgeschlagen."));
  };

  if (loading || !business) {
    return (
      <div className="text-muted-foreground text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
      </div>
    );
  }

  const nfcLink = business.nfc_link;
  const reviewUrl = business.google_review_url;

  return (
    <div className="space-y-6" data-testid="portal-business-detail">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/customer/business"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
          data-testid="portal-back-btn"
        >
          <ArrowLeft className="w-3 h-3" /> Zurück
        </Link>
        <Badge
          variant="outline"
          className={
            business.status === "active"
              ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
              : "border-white/10 text-muted-foreground"
          }
        >
          {business.status === "active" ? "Aktiv" : "Inaktiv"}
        </Badge>
      </div>

      <div>
        <p className="label-eyebrow">Firma</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 break-words">
          {business.business_name}
        </h1>
        <div className="mt-1.5 text-sm text-muted-foreground flex items-start gap-2">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="break-words">{business.address}</span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Scans gesamt" value={business.total_scans} icon={ScanLine} />
        <Kpi label="Heute" value={business.scans_today} icon={ScanLine} tone="primary" />
        <Kpi
          label="Google-Rating"
          value={
            typeof business.google_rating === "number"
              ? business.google_rating.toFixed(1)
              : "—"
          }
          icon={Star}
        />
        <Kpi
          label="Letzter Scan"
          value={business.last_scan_at ? fmt(business.last_scan_at) : "—"}
          icon={Clock}
          small
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-surface p-5" data-testid="portal-info-card">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Firmendaten</h3>
            </div>
            <dl className="space-y-3">
              <Row label="Firmenname" value={business.business_name} />
              <Row label="Adresse" value={business.address} />
              <Row label="Place ID" value={business.place_id || "—"} mono />
              <Row label="Slug" value={business.slug} mono />
              <Row label="Google-Bewertungslink" value={reviewUrl} mono link />
              <Row label="NFC-Link" value={nfcLink} mono link />
              <Row
                label="Google-Rating"
                value={
                  typeof business.google_rating === "number"
                    ? `${business.google_rating.toFixed(1)} (${business.google_rating_count ?? 0} Bewertungen)`
                    : "—"
                }
              />
            </dl>
          </div>

          <div className="card-surface p-5" data-testid="portal-scans-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">Letzte Scans</h3>
              <span className="text-xs text-muted-foreground">
                {scans.length} Ereignisse
              </span>
            </div>
            {scans.length ? (
              <ul className="divide-y divide-white/[0.05]">
                {scans.map((s) => (
                  <li key={s.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium">{fmt(s.timestamp)}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.device_type || "—"} · {s.browser || "—"} · {s.operating_system || "—"}
                      </div>
                    </div>
                    <Badge variant="outline" className="border-white/10 text-muted-foreground">
                      {s.device_type || "—"}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-white/[0.08] rounded-lg">
                Noch keine Scans erfasst.
              </div>
            )}
          </div>
        </div>

        {/* QR side */}
        <div className="space-y-6">
          <div className="card-surface p-5" data-testid="portal-qr-card">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="w-4 h-4 text-primary" />
              <h3 className="font-heading text-lg font-semibold">QR-Code & Links</h3>
            </div>
            <div className="bg-white rounded-xl p-5 flex items-center justify-center">
              <QRCodeCanvas
                value={nfcLink || "https://1ai-agency.com"}
                size={220}
                level="H"
                marginSize={2}
                fgColor="#0a0a0a"
                bgColor="#ffffff"
                data-testid="portal-qr-canvas"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-3 break-all">
              <LinkIcon className="w-3 h-3 inline mr-1" />
              {nfcLink}
            </p>
            <Separator className="my-4 bg-white/[0.06]" />
            <div className="flex flex-col gap-2">
              <Button
                onClick={downloadQr}
                className="w-full rounded-full gap-2"
                data-testid="portal-download-qr-btn"
              >
                <Download className="w-4 h-4" /> QR PNG herunterladen
              </Button>
              <Button
                onClick={() => copy(nfcLink, "NFC-Link")}
                variant="secondary"
                className="w-full rounded-full gap-2"
                data-testid="portal-copy-nfc-btn"
              >
                <Copy className="w-4 h-4" /> NFC-Link kopieren
              </Button>
              <Button
                onClick={() => copy(reviewUrl, "Bewertungslink")}
                variant="secondary"
                className="w-full rounded-full gap-2"
                data-testid="portal-copy-review-btn"
              >
                <Copy className="w-4 h-4" /> Bewertungslink kopieren
              </Button>
              <a
                href={nfcLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="portal-preview-btn"
              >
                <Button variant="ghost" className="w-full rounded-full gap-2">
                  <Eye className="w-4 h-4" /> Öffentliche Seite testen
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone, small }) {
  return (
    <div className="card-surface p-4">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon
          className={`w-4 h-4 ${
            tone === "primary" ? "text-primary" : "text-muted-foreground"
          }`}
        />
      </div>
      <div
        className={`mt-2 font-heading font-bold ${
          small ? "text-sm" : "text-2xl"
        } ${tone === "primary" ? "text-primary" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}

function Row({ label, value, mono, link }) {
  return (
    <div className="grid sm:grid-cols-[180px,1fr] gap-1 sm:gap-4 items-start">
      <div className="label-eyebrow pt-1.5">{label}</div>
      <div
        className={`text-sm py-1.5 break-all ${
          mono
            ? "font-mono text-xs bg-white/[0.02] px-3 py-2 rounded border border-white/[0.05]"
            : ""
        }`}
      >
        {link && typeof value === "string" && value.startsWith("http") ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {value}
          </a>
        ) : (
          value || "—"
        )}
      </div>
    </div>
  );
}
